import { Component, EventEmitter, OnDestroy, OnInit, AfterViewInit, Output, ChangeDetectorRef, ElementRef, ViewChild, HostListener, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpClient, HttpEventType } from '@angular/common/http';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as _ from 'lodash-es';
import moment from 'moment';
import { BehaviorSubject, Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  gridPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';
import { AddAttachmentsService } from './add-attachments.service';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';

// Allowed file extensions for upload
const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'svg', 'pdf', 'xls', 'xlsx', 'txt', 'doc', 'docx', 'ico', 'ppt', 'pptx', 'ods', 'wpd', 'rtf'];

// File icons mapping
const FILE_ICONS: { [key: string]: string } = {
  'pdf': 'fa-file-pdf-o',
  'doc': 'fa-file-word-o',
  'docx': 'fa-file-word-o',
  'xls': 'fa-file-excel-o',
  'xlsx': 'fa-file-excel-o',
  'ppt': 'fa-file-powerpoint-o',
  'pptx': 'fa-file-powerpoint-o',
  'txt': 'fa-file-text-o',
  'jpg': 'fa-file-image-o',
  'jpeg': 'fa-file-image-o',
  'png': 'fa-file-image-o',
  'svg': 'fa-file-image-o',
  'default': 'fa-file-o'
};

interface EntityData {
  row_id: string;
  data: any;
}

interface AttachmentFile {
  file_name: string;
  file_type: string;
  file_size: number;
  fS?: string;
  attachment_key: string;
  parent_key: string;
  parent_type: string;
  doc_batch_id: string;
  updated_by: string;
  created_by: string;
  created_on: string;
  icon?: string;
}

interface DocComment {
  doc_comment_key: string;
  message: string;
  display_name: string;
  date: string;
  dateM: number;
  sso_id: string;
  isSender: boolean;
  isUpdate?: boolean;
  isShowReplySubMenu?: boolean;
  replyMessage?: string;
  avatar_path?: string;
}

@Component({
    selector: 'gtw-add-attachments',
    templateUrl: './add-attachments.component.html',
    styleUrls: ['./add-attachments.component.scss'],
    animations: [
        trigger('collapseAnimation', [
            state('expanded', style({
                height: '*',
                opacity: 1,
                overflow: 'hidden'
            })),
            state('collapsed', style({
                height: '0px',
                opacity: 0,
                overflow: 'hidden',
                padding: '0 15px'
            })),
            transition('expanded <=> collapsed', [
                animate('300ms ease-in-out')
            ])
        ])
    ],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class AddAttachmentsComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();
  @ViewChild('fileInput') fileInput!: ElementRef;
  @ViewChild('modalHeader') modalHeader!: ElementRef;

  private suppressInitialFocus = true;

  @HostListener('focusin', ['$event'])
  onFocusIn(_event: Event): void {
    const event = _event as FocusEvent;
    if (this.suppressInitialFocus) {
      (event.target as HTMLElement)?.blur();
    }
  }

  private readonly subs = new Subscription();

  // Component state
  title: string = 'Add Attachements';
  minimise: boolean = false;
  btnDisabled: boolean = false;
  crudLoading: boolean = false;
  showFormLoader: boolean = false;
  loadingComments: boolean = false;
  attachmentsMinimise: boolean = false;
  
  // Mode detection - determines Add vs Edit mode
  // 'addAttachment' = Add mode (batch selection, no loading existing)
  // 'editAttachment' = Edit mode (single row, loads existing attachments)
  isEditMode: boolean = false;
  modalName: string = '';
  modalConfig: any = {};

  // Entity data
  entities: EntityData[] = [];

  // Attachment data
  attachmentTO: {
    attachmented_files: string[];
    attachment_file: string[];
    stored_files: string[];
    isError: boolean;
    errorMessage: string;
    flow?: any;
  } = {
    attachmented_files: [],
    attachment_file: [],
    stored_files: [],
    isError: false,
    errorMessage: ''
  };

  attachmentList$ = new BehaviorSubject<AttachmentFile[]>([]);
  existingFilesList: AttachmentFile[] = [];
  removedFileList: AttachmentFile[] = [];
  uploadingFiles: { name: string; progress: number }[] = [];

  // Comments data
  docComments: DocComment[] = [];
  replyMessage: string = '';

  // Configuration
  showUploadAttach: boolean = true;
  enableDeleteIcn: boolean = false;
  enableEditAttachBtn: boolean = false;
  showUploadDeletePanel: boolean = false; // Controls visibility of Upload/Delete panel

  // Data from inputs
  rowData: any = {};
  gridData: any = {};
  colData: any = {};
  filterParams: any = {};
  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};
  baseURLs: any = {};
  // Mirrored from WorkspaceService.activeScreen so parameter parity matches
  // legacy editAttachmentController.getData() / saveAttachment(), which read
  // workspaceFactory.activeScreen.label and activeScreen.data.settings
  // directly. The standalone grid passes only `{rowData, colData}` as _data,
  // so these are NOT available on `this.gridData`.
  private activeScreenLabel: string = '';
  private activeScreenSettings: any = {};
  private activeScreenActivityName: string = '';

  // URLs
  uploadUrl: string = '';

  // Initialization flags
  private isInitialized: boolean = false;
  private isLoading: boolean = false;

  // Confirmation modal
  showConfirmModal: boolean = false;
  confirmModalConfig: ConfirmationModalConfig = {
    title: 'Remove Attachment',
    message: 'Are you sure you want to remove this attachment?',
    iconClass: 'fa fa-trash',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No'
  };
  private pendingRemoveFile: { file: string; index: number } | null = null;

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private http: HttpClient,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private addAttachmentsService: AddAttachmentsService
  ) { }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.suppressInitialFocus = false;
    }, 500);
  }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    this.isInitialized = false;
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header.globalParams || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.SERVER_CONFIG = header.serverConfigData || {};
    this.uploadUrl = this.addAttachmentsService.getUploadUrl(
      this.GENERAL_CONFIG?.base_url || this.baseURLs?.api || ''
    );
    this.filterParams = normalizeFilterParams(activeScreen);
    const row = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(row) ? (row[0] || {}) : row;
    this.gridData = gridPayloadFromActiveScreen(activeScreen) || {};
    this.colData =
      activeScreen?.data?.colData || activeScreen?.data?.col_data || {};

    // Cache the screen-level fields the legacy controller reads directly off
    // workspaceFactory.activeScreen. The standalone grid does not propagate
    // these into _data (and therefore not into this.gridData), so read them
    // from the active screen here.
    this.activeScreenLabel = activeScreen?.label || '';
    this.activeScreenSettings = activeScreen?.data?.settings || {};
    this.activeScreenActivityName = activeScreen?.activity_name || '';

    const rawModalConfig =
      activeScreen?.data?.modalConfig || activeScreen?.data?.modal_config || {};
    if (Object.keys(rawModalConfig).length > 0) {
      this.modalConfig = rawModalConfig;
    } else {
      const modalName =
        activeScreen?.data?.modalName || activeScreen?.data?.modal_name || '';
      if (modalName) {
        this.modalConfig = { modal_name: modalName };
      }
    }
    this.applyModalConfigPayload(this.modalConfig);
  }

  /** Mirrors legacy @Input('modal-config') handling. */
  private applyModalConfigPayload(modalConfig: any): void {
    try {
      if (modalConfig && typeof modalConfig === 'object') {
        this.modalName = modalConfig.modal_name || modalConfig.callName || '';
        this.showUploadAttach = this.modalName.length > 0;
        this.enableDeleteIcn = modalConfig.enableDeleteIcn || false;

        this.isEditMode = this.modalName.toLowerCase().includes('edit');

        this.title = this.isEditMode ? 'Edit Attachment' : 'Add Attachements';

        this.showUploadDeletePanel = !this.isEditMode;
      }
    } catch (e) {
      console.log('Modal config from workspace not yet stable.', e);
    }
  }

  /**
   * Gate function to prevent duplicate initialization
   * Waits for ALL required inputs before initializing
   */
  private tryInit(): void {
    if (this.isInitialized || this.isLoading) {
      return;
    }

    // Check if we have minimum required data - GENERAL_CONFIG
    if (!this.GENERAL_CONFIG || Object.keys(this.GENERAL_CONFIG).length === 0) {
      console.log('AddAttachments tryInit: Waiting for GENERAL_CONFIG');
      return;
    }

    if (!this.modalName || this.modalName.length === 0) {
      console.log('AddAttachments tryInit: Waiting for modalConfig (modal_name)');
      return;
    }

    const hasEntities = this.gridData?.entities && _.isArray(this.gridData.entities) && this.gridData.entities.length > 0;
    const hasGridDataArray = _.isArray(this.gridData) && this.gridData.length > 0;
    const hasRowData = this.gridData?.rowData && Object.keys(this.gridData.rowData).length > 0;
    const hasFallbackRowData = this.rowData && Object.keys(this.rowData).length > 0;

    if (!hasEntities && !hasGridDataArray && !hasRowData && !hasFallbackRowData) {
      return;
    }

    this.isInitialized = true;
    this.init();
  }

  /**
   * Initialize component
   */
  private init(): void {
    try {
      // Setup entities from grid data
      this.setupEntities();

      // Set upload URL
      this.uploadUrl = this.addAttachmentsService.getUploadUrl(this.GENERAL_CONFIG?.base_url || this.baseURLs?.api || '');

      if (this.isEditMode) {
        console.log('AddAttachments: Edit mode - loading existing attachments');
        this.loadExistingAttachments();
      } else {
        console.log('AddAttachments: Add mode - skipping existing attachments load');
      }

      this.cdr.detectChanges();
    } catch (e) {
      console.error('Error in AddAttachments init:', e);
    }
  }

  /**
   * Load existing attachments from API
   * Maps to: getData() in AngularJS controller
   * 
   * Original code:
   * MessageFactory.getComments(GENERAL_CONFIG.base_url+'/loadFileAttachments', params)
   */
  private loadExistingAttachments(): void {
    // Mirror legacy editAttachmentController.getData(): read parent_key from
    // the row-level OBJECT_ID/object_id, and parent_type / doc_header_key
    // from workspaceFactory.activeScreen (label + data.settings.doc_header_key).
    // The migrated component must NOT rely on this.gridData for these fields
    // because in standalone NG12 the grid passes only `{rowData, colData}` as
    // _data and those screen-level values are not present.
    const entityData = this.entities.length > 0 ? this.entities[0].data : this.rowData;

    if (!entityData || Object.keys(entityData).length === 0) {
      console.log('AddAttachments: No entity data for loading attachments');
      return;
    }

    const parent_key = entityData.OBJECT_ID || entityData.object_id || '';

    if (!parent_key) {
      console.log('AddAttachments: No parent_key for loading attachments');
      return;
    }

    const docHeaderKeySetting =
      this.activeScreenSettings?.doc_header_key ||
      this.gridData?.settings?.doc_header_key;
    const doc_header_key = docHeaderKeySetting ? (entityData[docHeaderKeySetting] || '') : '';

    const parent_type =
      this.activeScreenLabel ||
      this.gridData?.screenLabel ||
      this.gridData?.activity_name ||
      this.activeScreenActivityName ||
      '';

    const params = {
      parent_key: parent_key,
      parent_type: parent_type,
      doc_keys: entityData.DOC_KEYS || entityData.doc_keys || '',
      doc_header_key: doc_header_key
    };

    console.log('AddAttachments: Loading existing attachments with params:', params);

    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api || '';
    
    this.addAttachmentsService.loadAttachments(baseUrl, params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (res: any) => {
        this.attachmentTO.attachmented_files = [];
        this.attachmentTO.stored_files = [];
        
        if (res?.comment?.attachmentList && res.comment.attachmentList.length > 0) {
          const attachments = res.comment.attachmentList;
          
          for (const attachment of attachments) {
            const fileSize = parseInt(attachment.file_size, 10) || 0;
            attachment.fS = this.formatBytes(fileSize);
            attachment.icon = this.getFileIcon(attachment.file_type);
            this.attachmentTO.attachmented_files.push(attachment.file_name);
          }
          
          this.attachmentList$.next(attachments);
          
          this.attachmentTO.attachmented_files = _.uniq(this.attachmentTO.attachmented_files);
          this.attachmentTO.stored_files = _.cloneDeep(this.attachmentTO.attachmented_files);
          this.existingFilesList = _.cloneDeep(attachments);
          
          this.checkEditPermissions();
          
          if (this.isEditMode) {
            this.enableEditAttachBtn = true;
          }
        }
        
        if (res?.comment?.docCommentsList) {
          this.docComments = res.comment.docCommentsList;
        }
      },
      (err: any) => {
        console.error('Error loading attachments:', err);
      }
    );
  }

  /**
   * Check if user can edit attachments based on permissions
   * Maps to: enabling enableEditAttachBtn in AngularJS
   */
  private checkEditPermissions(): void {
    const userSsoId = this.USER_SETTINGS?.user?.sso_id;
    const systemAccessLevel = this.USER_SETTINGS?.user?.system_access_level || 0;
    
    for (const item of this.attachmentList$.value) {
      if ((item.created_by && item.created_by === userSsoId) || systemAccessLevel >= 3) {
        this.enableEditAttachBtn = true;
        break;
      }
    }
  }

  /**
   * Setup entities from grid data
   * Maps to: init() in AngularJS controller
   * 
   * The AngularJS controller now always provides entities array in gridData.entities
   * This includes both single and batch selections, already formatted as {row_id, data}
   */
  private setupEntities(): void {
    
    // Primary source: gridData.entities (always provided by AngularJS controller)
    if (this.gridData?.entities && _.isArray(this.gridData.entities) && this.gridData.entities.length > 0) {
      this.entities = _.clone(this.gridData.entities);
    } 
    // Fallback: gridData is array (legacy batch selection)
    else if (_.isArray(this.gridData) && this.gridData.length > 0) {
      this.entities = _.clone(this.gridData);
    } 
    // Fallback: create entity from rowData or gridData.rowData
    else {
      const sourceData = this.gridData?.rowData || this.rowData || {};
      
      if (Object.keys(sourceData).length > 0) {
        const obj: EntityData = {
          row_id: sourceData.object_id || sourceData.OBJECT_ID || sourceData.row_id || '',
          data: sourceData
        };
        this.entities = [obj];
      } else {
        // Last resort - empty entities
        this.entities = [];
      }
    }
  }

  /**
   * Remove entity from selection
   * Maps to: vm.removeEntity = function(_index)
   */
  removeEntity(index: number): void {
    this.entities.splice(index, 1);
    if (this.entities.length === 0) {
      this.cancel();
    }
    this.cdr.detectChanges();
  }

  /**
   * Handle file selection
   */
  onFileSelected(event: any): void {
    const files: FileList = event.target.files;
    if (files && files.length > 0) {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (this.isValidFile(file)) {
          this.uploadFile(file);
        }
      }
    }
    // Reset input
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  /**
   * Validate file extension
   */
  private isValidFile(file: File): boolean {
    const extension = file.name.split('.').pop()?.toLowerCase() || '';
    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      this.attachmentTO.isError = true;
      this.attachmentTO.errorMessage = `Invalid file type: ${extension}`;
      this.cdr.detectChanges();
      setTimeout(() => {
        this.attachmentTO.isError = false;
        this.cdr.detectChanges();
      }, 5000);
      return false;
    }
    return true;
  }

  /**
   * Upload file to server using flow.js compatible format
   * Flow.js sends these parameters:
   * - flowChunkNumber: 1
   * - flowChunkSize: 104857600 (100MB)
   * - flowCurrentChunkSize: <file size>
   * - flowTotalSize: <file size>
   * - flowIdentifier: <size>-<filename with special chars replaced>
   * - flowFilename: <filename>
   * - flowRelativePath: <filename>
   * - flowTotalChunks: 1
   * - file: (binary)
   */
  private uploadFile(file: File): void {
    const uploadingFile = { name: file.name, progress: 0 };
    this.uploadingFiles.push(uploadingFile);
    this.cdr.detectChanges();

    const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9\s.]/gi, '_');
    const flowIdentifier = `${file.size}-${sanitizedFileName.replace(/[^a-zA-Z0-9]/g, '')}`;

    const formData = new FormData();
    formData.append('flowChunkNumber', '1');
    formData.append('flowChunkSize', '104857600');
    formData.append('flowCurrentChunkSize', file.size.toString());
    formData.append('flowTotalSize', file.size.toString());
    formData.append('flowIdentifier', flowIdentifier);
    formData.append('flowFilename', sanitizedFileName);
    formData.append('flowRelativePath', sanitizedFileName);
    formData.append('flowTotalChunks', '1');
    formData.append('file', file, sanitizedFileName);

    this.http.post(this.uploadUrl, formData, {
      reportUploadProgress: true,
      observe: 'events',
      withCredentials: true
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (event) => {
        if (event.type === HttpEventType.UploadProgress) {
          uploadingFile.progress = Math.round(100 * (event.loaded || 0) / (event.total || 1));
          this.cdr.detectChanges();
        } else if (event.type === HttpEventType.Response) {
          const idx = this.uploadingFiles.indexOf(uploadingFile);
          if (idx > -1) {
            this.uploadingFiles.splice(idx, 1);
          }
          const response: any = event.body;
          if (response?.uploadStatus === 'success' || response?.callSuccess === '1' || response?.callSuccess === 1) {
            const uploadedFileName = response.flowFilename || sanitizedFileName;
            this.attachmentTO.attachmented_files.push(uploadedFileName);
            this.attachmentTO.attachment_file.push(uploadedFileName);
            this._snackBar.open('File uploaded successfully', 'Close', {
              duration: 3000,
              panelClass: ['success-snackbar']
            });
          } else {
            this.handleUploadError(response?.errorMessage || response?.message || 'Upload failed');
          }
          this.cdr.detectChanges();
        }
      },
      (error) => {
        const idx = this.uploadingFiles.indexOf(uploadingFile);
        if (idx > -1) {
          this.uploadingFiles.splice(idx, 1);
        }
        this.handleUploadError('Network error during upload');
        this.cdr.detectChanges();
      }
    );
  }

  /**
   * Handle upload error
   */
  private handleUploadError(message: string): void {
    this.attachmentTO.isError = true;
    this.attachmentTO.errorMessage = message;
    this._snackBar.open(message, 'Close', {
      duration: 4000,
      panelClass: ['error-snackbar']
    });
    setTimeout(() => {
      this.attachmentTO.isError = false;
      this.cdr.detectChanges();
    }, 5000);
  }


  /**
   * Remove attachment from pending list
   * Maps to: vm.removeAttachment(ctrl, file, $index)
   */
  removeAttachment(file: string, index: number): void {
    const fileIndex = this.attachmentTO.attachmented_files.indexOf(file);
    if (fileIndex > -1) {
      this.attachmentTO.attachmented_files.splice(fileIndex, 1);
    }
    const attachFileIndex = this.attachmentTO.attachment_file.indexOf(file);
    if (attachFileIndex > -1) {
      this.attachmentTO.attachment_file.splice(attachFileIndex, 1);
    }
    this.cdr.detectChanges();
  }

  /**
   * Remove file (handles both new and existing files)
   * Maps to: vm.revmoveFile(file, index)
   * Shows confirmation modal before removing
   */
  revmoveFile(file: string, index: number): void {
    const existingFile = _.find(this.attachmentList$.value, { file_name: this.extractFileName(file) });
    
    if (existingFile) {
      // Existing file, check permissions first
      const canDelete = (existingFile.created_by && existingFile.created_by === this.USER_SETTINGS?.user?.sso_id) ||
                        (this.USER_SETTINGS?.user?.system_access_level >= 3);
      
      if (!canDelete) {
        this._snackBar.open('Cannot delete unauthorized files', 'Close', {
          duration: 4000,
          panelClass: ['warning-snackbar']
        });
        return;
      }
    }
    
    // Store pending remove and show confirmation modal
    this.pendingRemoveFile = { file, index };
    this.confirmModalConfig = {
      ...this.confirmModalConfig,
      message: `Are you sure you want to remove attachment: ${this.extractFileName(file)}?`
    };
    this.showConfirmModal = true;
    this.cdr.detectChanges();
  }

  /**
   * Handle confirmation modal confirm action
   */
  onRemoveConfirm(): void {
    if (this.pendingRemoveFile) {
      const { file, index } = this.pendingRemoveFile;
      this.performRemoveFile(file, index);
    }
    this.showConfirmModal = false;
    this.pendingRemoveFile = null;
    this.cdr.detectChanges();
  }

  /**
   * Handle confirmation modal cancel action
   */
  onRemoveCancel(): void {
    this.showConfirmModal = false;
    this.pendingRemoveFile = null;
    this.cdr.detectChanges();
  }

  /**
   * Perform actual file removal after confirmation
   */
  private performRemoveFile(file: string, index: number): void {
    const fileName = this.extractFileName(file);
    const existingFile = _.find(this.attachmentList$.value, { file_name: fileName });
    
    if (!existingFile) {
      // New file - call removeAttachment API then remove from list
      this.callRemoveAttachmentAPI(fileName, () => {
        this.removeAttachment(file, index);
      });
    } else {
      // Existing file - add to removed list and remove from view
      this.callRemoveAttachmentAPI(fileName, () => {
        this.removeAttachment(file, index);
        this.removedFileList.push(existingFile);
      });
    }
  }

  /**
   * Call removeAttachment API
   * Maps to: vm.MessageFactory.CommentCrud(GENERAL_CONFIG.base_url + '/removeAttachment', objContext)
   */
  private callRemoveAttachmentAPI(fileName: string, onSuccess: () => void): void {
    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api || '';
    
    this.addAttachmentsService.removeAttachment(baseUrl, fileName).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (res: any) => {
        if (res.uploadStatus === 'callSuccess' || res.callSuccess === '1' || res.callSuccess === 1) {
          onSuccess();
          this._snackBar.open('Attachment removed successfully', 'Close', {
            duration: 3000,
            panelClass: ['success-snackbar']
          });
        } else {
          this._snackBar.open(res.errorMessage || 'Error removing attachment', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error removing attachment:', error);
        this._snackBar.open('Error removing attachment', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  /**
   * Extract filename from full path
   * e.g., "/app/static/upload_attachments/temp-xxx/test.txt" -> "test.txt"
   */
  extractFileName(filePath: string): string {
    if (!filePath) return '';
    // Get everything after the last slash
    const lastSlash = filePath.lastIndexOf('/');
    if (lastSlash === -1) {
      // Check for backslash (Windows paths)
      const lastBackslash = filePath.lastIndexOf('\\');
      return lastBackslash === -1 ? filePath : filePath.substring(lastBackslash + 1);
    }
    return filePath.substring(lastSlash + 1);
  }

  /**
   * Download attachment file
   * Maps to: vm.downloadAttachmentFile(fileName, commentKey, docKey)
   */
  downloadAttachmentFile(fileName: string, parentKey: string, docKey: string): void {
    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api || '';
    const clientKey = sessionStorage.getItem('client_key') || '';
    const ssoId = this.USER_SETTINGS?.user?.sso_id || '';

    this.addAttachmentsService.getApiAuthToken(baseUrl).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (res: any) => {
        if (res && res.apiToken) {
          const downloadUrl = this.addAttachmentsService.buildDownloadUrl(
            baseUrl, fileName, parentKey, docKey, clientKey, res.apiToken, ssoId
          );
          window.open(downloadUrl, '_blank');
        }
      },
      (error: any) => {
        console.error('Error getting auth token:', error);
        this._snackBar.open('Error downloading file', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
      }
    );
  }

  /**
   * Format bytes to human readable size
   * Maps to: vm.formatBytes(bytes)
   */
  formatBytes(bytes: number): string {
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    if (bytes === 0) return '0 Byte';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i)) + ' ' + sizes[i];
  }

  /**
   * Get file icon based on file type
   */
  getFileIcon(fileType: string): string {
    return FILE_ICONS[fileType?.toLowerCase()] || FILE_ICONS['default'];
  }

  /**
   * Calculate time ago string
   * Maps to: $scope.timeago(date)
   * Matches original AngularJS implementation exactly
   */
  timeago(date: string | Date): string {
    // Match original AngularJS: var date = new Date(date); return moment(date).fromNow();
    const jsDate = new Date(date as string);
    return moment(jsDate).fromNow();
  }

  /**
   * Show reply submenu for comment
   * Maps to: vm.showReplySubMenu(rM)
   */
  showReplySubMenu(comment: DocComment): void {
    // Close all other submenus
    this.docComments.forEach(c => {
      if (c !== comment) {
        c.isShowReplySubMenu = false;
      }
    });
    comment.isShowReplySubMenu = !comment.isShowReplySubMenu;
    this.cdr.detectChanges();
  }

  /**
   * Start editing comment
   */
  startEditComment(comment: DocComment): void {
    comment.isUpdate = true;
    comment.replyMessage = comment.message;
    comment.isShowReplySubMenu = false;
    this.cdr.detectChanges();
  }

  /**
   * Cancel editing comment
   */
  cancelEditComment(comment: DocComment): void {
    comment.isUpdate = false;
    comment.replyMessage = '';
    this.cdr.detectChanges();
  }

  /**
   * Update or delete document comment
   * Maps to: vm.updateDocMessage(docComment, type, index)
   */
  updateDocMessage(comment: DocComment, type: 'update' | 'delete', index: number): void {
    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api || '';
    
    const params = {
      parent_key: this.rowData?.OBJECT_ID || this.rowData?.object_id || '',
      parent_type: this.getScreenLabel(),
      doc_purpose: type === 'update' ? (comment.replyMessage || comment.message) : comment.message,
      doc_comment_key: comment.doc_comment_key,
      type: type,
      doc_header_key: this.getDocHeaderKey()
    };

    this.addAttachmentsService.saveDocComment(baseUrl, params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (res: any) => {
        if (res.callSuccess == 1 || res.callSuccess === '1') {
          this._snackBar.open('Comment saved successfully', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          
          if (type === 'delete') {
            this.docComments.splice(index, 1);
          } else {
            comment.message = comment.replyMessage || comment.message;
            comment.isUpdate = false;
          }
        } else {
          this._snackBar.open(res.errorMessage || 'Error saving comment', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error updating comment:', error);
        this._snackBar.open('Error saving comment', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
      }
    );
  }

  /**
   * Add document comment
   * Maps to: vm.addDocComment($event)
   */
  addDocComment(event?: KeyboardEvent): void {
    // Only trigger on Enter key or direct call
    if (event && event.key !== 'Enter') {
      return;
    }

    if (!this.replyMessage || this.replyMessage.trim() === '') {
      return;
    }

    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api || '';
    
    const params = {
      parent_key: this.rowData?.OBJECT_ID || this.rowData?.object_id || '',
      parent_type: this.getScreenLabel(),
      doc_purpose: this.replyMessage,
      doc_comment_key: '',
      type: 'add',
      doc_header_key: this.getDocHeaderKey()
    };

    this.addAttachmentsService.saveDocComment(baseUrl, params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (res: any) => {
        if (res.callSuccess == 1 || res.callSuccess === '1') {
          if (res.replyList) {
            this.docComments = res.replyList;
            this.processComments();
          }
          this.replyMessage = '';
        } else {
          this._snackBar.open(res.errorMessage || 'Error adding comment', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error adding comment:', error);
        this._snackBar.open('Error adding comment', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
      }
    );
  }

  /**
   * Process comments to add computed properties
   */
  private processComments(): void {
    const currentSsoId = this.USER_SETTINGS?.user?.sso_id;
    this.docComments.forEach(comment => {
      comment.dateM = new Date(comment.date).getTime();
      comment.isSender = comment.sso_id === currentSsoId;
    });
    // Sort by date descending
    this.docComments.sort((a, b) => b.dateM - a.dateM);
  }

  /**
   * Get screen label from workspace
   * Mirrors legacy: workspaceFactory.activeScreen.label
   */
  private getScreenLabel(): string {
    return (
      this.activeScreenLabel ||
      this.gridData?.screenLabel ||
      this.activeScreenActivityName ||
      this.gridData?.activity_name ||
      this.filterParams?.screen_name ||
      'attachment'
    );
  }

  /**
   * Get doc header key value from current row using the screen's settings.
   * Mirrors legacy: rowData[workspaceFactory.activeScreen.data.settings.doc_header_key]
   */
  private getDocHeaderKey(): string {
    const docHeaderKey =
      this.activeScreenSettings?.doc_header_key ||
      this.gridData?.settings?.doc_header_key;
    const source = (this.entities && this.entities[0]?.data) || this.rowData;
    if (docHeaderKey && source) {
      return source[docHeaderKey] || '';
    }
    return '';
  }

  /**
   * Save attachments
   * Maps to: vm.saveAttachment()
   */
  saveAttachment(): void {
    this.btnDisabled = true;
    this.crudLoading = true;
    this.cdr.detectChanges();

    // Validate
    if (!this.attachmentTO.attachmented_files || this.attachmentTO.attachmented_files.length === 0) {
      this._snackBar.open('Please add documents before save', 'Close', {
        duration: 4000,
        panelClass: ['warning-snackbar']
      });
      this.btnDisabled = false;
      this.crudLoading = false;
      this.cdr.detectChanges();
      return;
    }

    // Build trans_json
    // Settings come from workspaceFactory.activeScreen.data.settings (cached
    // in activeScreenSettings) to match legacy editAttachmentController
    // saveAttachment(): rowData[settings.doc_header_key].
    const trans_json: any[] = [];
    for (const entity of this.entities) {
      if (entity.row_id) {
        const docHeaderKey =
          this.activeScreenSettings?.doc_header_key ||
          this.gridData?.settings?.doc_header_key;
        const headerType =
          this.activeScreenSettings?.doc_header_type ||
          this.gridData?.settings?.doc_header_type;

        trans_json.push({
          parent_key: entity.row_id,
          parent_type: this.getScreenLabel(),
          combination_keys: entity.data?.COMBINATION_KEY,
          object_id: entity.row_id,
          combination_key: entity.data?.COMBINATION_KEY,
          me_key: entity.data?.ME_KEY || '',
          doc_header_key: docHeaderKey ? entity.data?.[docHeaderKey] : '',
          header_type: headerType ? entity.data?.[headerType] : this.getScreenLabel()
        });
      }
    }

    // Build oldFiles
    const oldFiles: any[] = [];
    if (this.removedFileList && this.removedFileList.length > 0) {
      this.removedFileList.forEach(item => {
        oldFiles.push({
          parent_key: item.parent_key,
          parent_type: item.parent_type,
          doc_key: item.attachment_key,
          file_name: item.file_name,
          doc_batch_id: item.doc_batch_id
        });
      });
    }

    // Build params - mirror legacy editAttachmentController saveAttachment():
    //   screen_key  = workspaceFactory.activeScreen.screen_key
    //   activity_key = workspaceFactory.activeScreen.activity_key
    //   grid_key    = workspaceFactory.activeScreen.data.settings.grid_key
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const screenKey =
      activeScreen?.screen_key ||
      this.gridData?.screen_key ||
      this.filterParams?.screen_key ||
      '-100';
    const activityKey =
      activeScreen?.activity_key ||
      this.gridData?.activity_key ||
      this.filterParams?.activity_key ||
      '-102';
    const gridKey =
      this.activeScreenSettings?.grid_key ||
      this.gridData?.settings?.grid_key ||
      '-103';
    const filterCached = this.filterParams || {};

    const rowKey = this.gridData?.selected?.row_id || '';
    const colKey = this.gridData?.selected?.col_id || '';
    const tableState = this.gridData?.tableState || {};
    const element = this.gridData?.selected?.select_type || '';

    const filterJson = {
      activity_key: activityKey,
      screen_key: screenKey,
      filter_json: filterCached,
      filters: [
        this.activeScreenSettings?.doc_header_key ||
          this.gridData?.settings?.doc_header_key
      ]
    };

    const objContext: any = {
      state: 'messages',
      tableState: JSON.stringify(tableState),
      data: JSON.stringify(this.gridData?.selected || {}),
      type: 'grid',
      screen_key: screenKey,
      activity_key: activityKey,
      row_key: rowKey,
      col_key: colKey,
      element: element,
      callNameA: this.gridData?.callName || 'addAttachments',
      value: '',
      trans_json: JSON.stringify(trans_json),
      parent_key: '',
      attachments: this.attachmentTO.attachment_file.join(','),
      oldFiles: JSON.stringify(oldFiles),
      docType: 'transaction',
      filters: JSON.stringify(filterJson),
      doc_purpose: this.replyMessage,
      doc_comment_key: '',
      grid_key: gridKey,
      screen_name: this.getScreenLabel(),
      activity_name: this.activeScreenActivityName || this.gridData?.activity_name || ''
    };

    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api || '';

    this.addAttachmentsService.saveAttachments(baseUrl, objContext).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.crudLoading = false;
        this.btnDisabled = false;

        if (data.callSuccess === '1' || data.callSuccess === 1) {
          this._snackBar.open('Documents Saved Successfully', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });

          this.requestGridUpdate.emit('refresh');
        } else {
          const errorMessage = data.errorMessage || 'Error occurred fetching data. If this continues please contact support';
          this._snackBar.open(errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.crudLoading = false;
        this.btnDisabled = false;
        console.error('Error saving attachments:', error);
        this._snackBar.open('Error occurred while saving', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  /**
   * Cancel and close modal
   * Maps to: vm.cancel()
   */
  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  /**
   * Show upload/delete attachments panel
   * Called when user clicks "Edit/Add Attachments" button in Edit mode
   */
  showUploadPanel(): void {
    this.showUploadDeletePanel = true;
    this.enableDeleteIcn = true;
  }

  /**
   * Trigger file input click
   */
  triggerFileInput(): void {
    if (this.fileInput) {
      this.fileInput.nativeElement.click();
    }
  }
}
